export type OAuthCallbackPlatform = "android" | "macos";

type OAuthCallbackConfig = {
  readonly pathname: string;
  readonly target: string;
  readonly label: string;
};

const CALLBACK_CONFIG: Record<OAuthCallbackPlatform, OAuthCallbackConfig> = {
  android: {
    pathname: "/auth/android/callback",
    target: "com.sessapps.kaikei://oauth/callback",
    label: "Android",
  },
  macos: {
    pathname: "/auth/macos/callback",
    target: "kaikei-macos://oauth/callback",
    label: "macOS",
  },
};

const ALLOWED_RESPONSE_PARAMETERS = new Set([
  "code",
  "state",
  "iss",
  "error",
  "error_description",
]);
const MAX_PARAMETER_LENGTH = 4096;
const UNSAFE_VALUE = /[\u0000-\u001f\u007f\u2028\u2029]/u;

export type OAuthCallbackResult =
  | {
      readonly ok: true;
      readonly platform: OAuthCallbackPlatform;
      readonly targetUrl: string;
      readonly parameters: ReadonlyArray<readonly [string, string]>;
    }
  | {
      readonly ok: false;
      readonly platform: OAuthCallbackPlatform;
      readonly message: string;
    };

function failure(
  platform: OAuthCallbackPlatform,
  message: string,
): OAuthCallbackResult {
  return { ok: false, platform, message };
}

function hasValidIssuer(value: string): boolean {
  try {
    const issuer = new URL(value);
    return (
      issuer.protocol === "https:" &&
      !issuer.username &&
      !issuer.password &&
      !issuer.search &&
      !issuer.hash
    );
  } catch {
    return false;
  }
}

function hasValidQueryEncoding(query: string): boolean {
  try {
    // URLSearchParams treats `+` as a space. Replace it only for validation;
    // the original query is still parsed below so values retain their meaning.
    decodeURIComponent(query.replaceAll("+", "%20"));
    return true;
  } catch {
    return false;
  }
}

function hasFragmentSyntax(input: string, url: URL): boolean {
  // URL.hash is empty for a trailing `#`, so inspect the original URL as well.
  return url.hash.length > 0 || input.includes("#");
}

function rawPath(input: string): string | null {
  const schemeEnd = input.indexOf("://");
  if (schemeEnd < 0) return null;
  const pathStart = input.indexOf("/", schemeEnd + 3);
  if (pathStart < 0) return "/";
  const queryStart = input.indexOf("?", pathStart);
  const fragmentStart = input.indexOf("#", pathStart);
  const pathEnd =
    [queryStart, fragmentStart]
      .filter((index) => index >= 0)
      .sort((a, b) => a - b)[0] ?? input.length;
  return input.slice(pathStart, pathEnd);
}

export function callbackPlatformForPath(
  pathname: string,
): OAuthCallbackPlatform | null {
  if (pathname === CALLBACK_CONFIG.android.pathname) return "android";
  if (pathname === CALLBACK_CONFIG.macos.pathname) return "macos";
  return null;
}

export function isOAuthCallbackPath(pathname: string): boolean {
  return callbackPlatformForPath(pathname) !== null;
}

/**
 * Validate an OIDC authorization response and build the fixed native-app URI.
 *
 * This deliberately accepts no target/scheme from the request. The only
 * values copied to the app URI are the standard response parameters, each
 * re-encoded by URLSearchParams.
 */
export function parseOAuthCallback(
  input: string,
  platform: OAuthCallbackPlatform,
): OAuthCallbackResult {
  const config = CALLBACK_CONFIG[platform];
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return failure(platform, "認証コールバックのURLが不正です。");
  }

  if (!/^https?:$/u.test(url.protocol) || url.username || url.password) {
    return failure(platform, "認証コールバックのURLが不正です。");
  }
  if (url.pathname !== config.pathname) {
    return failure(platform, "認証コールバックのパスが一致しません。");
  }
  // Do not let URL canonicalization turn dot segments or encoded path
  // separators into one of the registered callback paths.
  if (rawPath(input) !== config.pathname) {
    return failure(platform, "認証コールバックのパスが不正です。");
  }
  if (hasFragmentSyntax(input, url)) {
    return failure(
      platform,
      "認証コールバックにフラグメントは指定できません。",
    );
  }
  if (!url.search || url.search.length <= 1) {
    return failure(platform, "認証コールバックのパラメーターがありません。");
  }

  const rawQuery = url.search.slice(1);
  if (
    !hasValidQueryEncoding(rawQuery) ||
    rawQuery.split("&").some((part) => part.length === 0)
  ) {
    return failure(platform, "認証コールバックの形式が不正です。");
  }

  // Parameter names are fixed ASCII OIDC names. Reject encoded or otherwise
  // ambiguous names before URLSearchParams decodes them.
  if (
    rawQuery
      .split("&")
      .some(
        (part) =>
          !part.includes("=") || part.slice(0, part.indexOf("=")).includes("%"),
      )
  ) {
    return failure(platform, "認証コールバックのパラメーター名が不正です。");
  }

  const parameters = [...new URLSearchParams(rawQuery).entries()];
  const seen = new Set<string>();
  for (const [name, value] of parameters) {
    if (!ALLOWED_RESPONSE_PARAMETERS.has(name) || seen.has(name)) {
      return failure(
        platform,
        "認証コールバックに許可されていないパラメーターがあります。",
      );
    }
    if (
      value.length === 0 ||
      value.length > MAX_PARAMETER_LENGTH ||
      UNSAFE_VALUE.test(value)
    ) {
      return failure(platform, "認証コールバックの値が不正です。");
    }
    seen.add(name);
  }

  const values = new Map(parameters);
  const code = values.get("code");
  const state = values.get("state");
  const error = values.get("error");
  const errorDescription = values.get("error_description");
  const issuer = values.get("iss");

  if (
    !state ||
    !!code === !!error ||
    (errorDescription !== undefined && !error) ||
    (issuer !== undefined && !hasValidIssuer(issuer))
  ) {
    return failure(platform, "認証コールバックの応答が不完全または不正です。");
  }

  const targetQuery = new URLSearchParams(parameters).toString();
  return {
    ok: true,
    platform,
    targetUrl: `${config.target}?${targetQuery}`,
    parameters,
  };
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/gu,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        character
      ] ?? character,
  );
}

function callbackPage(result: OAuthCallbackResult): string {
  if (!result.ok) {
    return `<main class="callback-page" aria-labelledby="callback-title"><div class="callback-card callback-card-error"><p class="eyebrow">SECURE SIGN-IN</p><h1 id="callback-title">アプリに戻れませんでした</h1><p class="callback-description">認証応答を安全に確認できなかったため、アプリへの転送を中止しました。最初からもう一度お試しください。</p><a class="primary-button" href="/">会計クラウドへ戻る <span aria-hidden="true">→</span></a></div></main>`;
  }

  const config = CALLBACK_CONFIG[result.platform];
  const target = escapeHtml(result.targetUrl);
  return `<main class="callback-page" aria-labelledby="callback-title"><div class="callback-card"><p class="eyebrow">SECURE SIGN-IN</p><h1 id="callback-title">${config.label}アプリへ戻ります</h1><p class="callback-description">認証が完了しました。自動でアプリに戻らない場合は、下のボタンをタップしてください。</p><a class="primary-button callback-link" href="${target}">アプリへ戻る <span aria-hidden="true">→</span></a><p class="callback-note">この画面では認証情報を保存しません。</p></div></main>`;
}

export function mountOAuthCallbackBridge(
  root: HTMLElement,
  input = window.location.href,
  autoRedirect = true,
): OAuthCallbackResult {
  const platform = callbackPlatformForPath(new URL(input).pathname);
  if (!platform) throw new Error("Unsupported OAuth callback path.");

  const result = parseOAuthCallback(input, platform);
  root.innerHTML = callbackPage(result);

  if (result.ok && autoRedirect) {
    window.setTimeout(() => window.location.replace(result.targetUrl), 250);
  }
  return result;
}
